import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import KeypadButton from '../components/KeypadButton';
import KeypadContainer from '../components/KeypadContainer';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import { useSnackbar } from '../contexts/SnackbarContext';

/**
 * Setup page guides users through the device setup process
 * Shows when the device is not authenticated or when setup is explicitly requested
 */
const SetupPage = () => {
  // Array of 6 input references
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  const urlActivation = useRef(false); // This is a silly hack to prevent double processing of the code param
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [activeInputIndex, setActiveInputIndex] = useState(0);
  const { showError } = useSnackbar();

  const { login } = useAuth();

  // Handle login attempt with useCallback to prevent dependency issues
  const handleSubmit = async (digits, $event = null) => {
    if ($event) {
      $event.preventDefault();
    }

    const code = digits.join('');

    if (code.length !== 6) {
      showError('Please enter all 6 digits');
      return;
    }

    setLoading(true);

    try {
      await login(code);

    } catch (err) {
      showError(err.message || 'Failed to activate device. Please check your code.');
    } finally {
      setLoading(false);
    }
  };

  // Check for 'code' query parameter on component mount
  useEffect(() => {
    // For some reason this code is rendered twice.  I haven't been able to figure out why
    // so I'm including a ref to prevent it from running twice
    const searchParams = new URLSearchParams(window.location.search);
    const codeParam = searchParams.get('code');

    if (codeParam && codeParam.length === 6 && /^\d+$/.test(codeParam) && !urlActivation.current) {
      // Remove it from the url to prevent double processing
      urlActivation.current = true;

      // Split the code into individual digits
      const codeDigits = codeParam.split('');
      setDigits(codeDigits);
      setActiveInputIndex(5);
      inputRefs[5].current.focus();
      handleSubmit(codeDigits);
    }
  }, []);

  // Handle individual digit input change
  const handleDigitChange = (index, e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');

    if (value.length <= 1) {
      const newDigits = [...digits];
      newDigits[index] = value;
      setDigits(newDigits);

      // Auto-focus next input field if value is entered
      if (value && index < 5) {
        setActiveInputIndex(index + 1);
        inputRefs[index + 1].current.focus();
      }
    }
  };

  // Handle key down for navigation and backspace
  const handleKeyDown = (index, e) => {
    // If backspace is pressed on an empty field, focus previous field
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      setActiveInputIndex(index - 1);
      inputRefs[index - 1].current.focus();
    }
    // Arrow left/right navigation between fields
    else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveInputIndex(index - 1);
      inputRefs[index - 1].current.focus();
    }
    else if (e.key === 'ArrowRight' && index < 5) {
      setActiveInputIndex(index + 1);
      inputRefs[index + 1].current.focus();
    }
  };

  // Handle focus on input field
  const handleInputFocus = (index) => {
    setActiveInputIndex(index);
  };

  // Handle paste event to distribute digits
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');

    if (pastedData) {
      const newDigits = [...digits];

      // Fill digits array with pasted numbers
      for (let i = 0; i < Math.min(pastedData.length, 6); i++) {
        newDigits[i] = pastedData[i];
      }

      setDigits(newDigits);

      // Focus the next empty field or last field
      const nextEmptyIndex = newDigits.findIndex(d => d === '');
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      setActiveInputIndex(focusIndex);
      inputRefs[focusIndex].current.focus();
    }
  };

  // Handle keypad button click
  const handleKeypadClick = (num) => {
    if (num === 'backspace') {
      // Handle backspace
      const newDigits = [...digits];

      if (newDigits[activeInputIndex]) {
        // If current field has a value, clear it
        newDigits[activeInputIndex] = '';
        setDigits(newDigits);
      } else if (activeInputIndex > 0) {
        // If current field is empty, move to previous field and clear it
        const newIndex = activeInputIndex - 1;
        newDigits[newIndex] = '';
        setDigits(newDigits);
        setActiveInputIndex(newIndex);
        inputRefs[newIndex].current.focus();
      }
    } else if (num === 'reset') {
      // Reset all digits and focus first field
      setDigits(['', '', '', '', '', '']);
      setActiveInputIndex(0);
      inputRefs[0].current.focus();
    } else {
      // Handle number input
      if (activeInputIndex <= 5) {
        const newDigits = [...digits];
        newDigits[activeInputIndex] = num.toString();
        setDigits(newDigits);

        // Move to next field if available
        if (activeInputIndex < 5) {
          const newIndex = activeInputIndex + 1;
          setActiveInputIndex(newIndex);
          inputRefs[newIndex].current.focus();
        }
      }
    }
  };

  return (
    <Container sx={{
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw !important',
      display: 'flex',
      flexDirection: 'row',
      padding: '0 !important',
      margin: '0 !important',
    }}>
      <Grid container sx={{
        height: '100%',
        width: '100%'
      }}>
        <Grid item size={6} sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            px: 3
          }}>
            <div>&nbsp;</div>
            <Box sx={{ maxWidth: '800px', width: '100%', mt: 8 }}>
              <Typography variant="h5" component="h1" gutterBottom align="center">
                <strong>Loyalty Device Setup</strong>
              </Typography>

              <Box my={2} textAlign='center'>
                <Typography paragraph>
                  To activate your device, an administrator needs to log into <a href="https://marketing.cinch.io" target='_blank'>https://marketing.cinch.io</a> to generate an activation code.
                </Typography>
              </Box>

              <form onSubmit={($event) => handleSubmit(digits, $event)}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                  <Grid container spacing={1} justifyContent="center">
                    {digits.map((digit, index) => (
                      <Grid item key={index}>
                        <TextField
                          inputRef={inputRefs[index]}
                          value={digit}
                          onChange={(e) => handleDigitChange(index, e)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          onFocus={() => handleInputFocus(index)}
                          onPaste={index === 0 ? handlePaste : undefined}
                          variant="outlined"
                          autoComplete="off"
                          inputProps={{
                            maxLength: 1,
                            inputMode: 'none',
                            pattern: '[0-9]',
                            readOnly: true,
                            style: {
                              fontSize: '2rem',
                              padding: '0.5rem',
                              width: '3rem',
                              height: '3rem',
                              textAlign: 'center',
                              caretColor: 'transparent'
                            }
                          }}
                          disabled={loading}
                          autoFocus={index === 0}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: index === activeInputIndex ? 'primary.main' : 'inherit',
                                borderWidth: index === activeInputIndex ? 2 : 1
                              }
                            }
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading || digits.some(d => d === '')}
                    sx={{ minWidth: '200px', mt: 2 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Activate Device'}
                  </Button>
                </Box>
              </form>
            </Box>

            <Box pb={2} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="body3" color="text.secondary">
                powered by
              </Typography>
              <Box ml={1} display="flex" alignItems="center">
                <CinchLogo
                  width={75}
                  style={{ marginRight: '4px' }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item size={6}>
          <KeypadContainer
            loading={loading}
            canSubmit={digits.every(d => d !== '')}
            onKeyPress={handleKeypadClick}
            onSubmit={() => handleSubmit(digits)}
            onBackspace={() => handleKeypadClick('backspace')}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default SetupPage;